import { useLocalStorage } from '@vueuse/core'
import type { Ref } from 'vue'
import type { LayoutSkeleton } from '~/composables/data-fetching/auto-layout/index.types'
import { DEFAULT_FEATURE_FLAGS } from '~/composables/feature-flags/DEFAULT_FEATURE_FLAGS'

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

export interface FeatureFlags {
  timestamp?: string
  name?: string
  layoutSkeleton: LayoutSkeleton
  summaryPage?: DeepPartial<SummaryPageSkeleton>
  sponsoredTiles?: SponsoredTiles
  colorScheme?: 'light' | 'dark' | 'system'
}

interface SponsoredTiles {
  provider: string
  tilesCount?: number
}

interface SummaryPageSkeleton {
  siderail: {
    topClass: string
    middleClass: string
    bottomClass: string
  }
  content: {
    topClass: string
    middleClass: string
    bottomContentAboveButtonClass: string
    bottomContentBelowButtonClass: string
  }
  bottomClass: string
}

interface UseFeatureFlag {
  maxTimeout: number
}

export function useFeatureFlag(options?: UseFeatureFlag): {
  featureFlag: Ref<FeatureFlags | null | undefined>
  state: Ref<'loading' | 'error' | 'success'>
} {
  const cachedFeatureFlag = useLocalStorage<FeatureFlags>('app:featureFlags', {} as any, {
    listenToStorageChanges: false,
  })

  const state = ref<'loading' | 'error' | 'success'>('loading')
  const tries = ref(0)
  const wasLayoutUpdated = ref(false)
  const newFeatureFlagSetting = ref<FeatureFlags>()
  const timeout = 10
  const maxTries = (options?.maxTimeout || 800) / timeout

  function synchronizeFeatureFlagsWithAy() {
    tries.value++
    const isNewFeatureFlagDetected = window?.aYield?.featureFlags
    if (isNewFeatureFlagDetected) {
      clearInterval(interval)
      const newFeatureFlagTimestamp = window.aYield.featureFlags?.timestamp
      if (newFeatureFlagTimestamp && window.aYield.featureFlags) {
        const previousSetting = cachedFeatureFlag.value?.timestamp
          ? new Date(cachedFeatureFlag.value?.timestamp)
          : undefined
        const newSetting = new Date(newFeatureFlagTimestamp)

        if (!previousSetting || newSetting > previousSetting) {
          wasLayoutUpdated.value = true
          newFeatureFlagSetting.value = window.aYield.featureFlags
          cachedFeatureFlag.value = window.aYield.featureFlags
        }
      }
    }

    if (tries.value >= maxTries) {
      clearInterval(interval)
      state.value = 'error'
    }
  }

  const interval = setInterval(synchronizeFeatureFlagsWithAy, timeout)

  const isThereACachedLayout = computed(() => {
    return cachedFeatureFlag.value?.layoutSkeleton
  })

  const featureFlag = computed(() => {
    if (isThereACachedLayout.value) {
      return cachedFeatureFlag.value
    }

    if (isThereACachedLayout.value && wasLayoutUpdated.value) {
      return cachedFeatureFlag.value
    }

    if (!isThereACachedLayout.value) {
      if (state.value === 'success') return newFeatureFlagSetting.value
      if (state.value === 'error') return DEFAULT_FEATURE_FLAGS
      if (state.value === 'loading') return null
    }
  })

  return {
    featureFlag,
    state,
  }
}
